import { NavLink, useNavigate } from "react-router";
import { House, LayoutList, Zap, ChevronLeft } from "lucide-react";
import { useAppServices } from "../hooks/AppContext.tsx";
import { useOnline, useUpdateState } from "../hooks/bindings.ts";
import styles from "../design-system/ui.module.css";

export function Frame({ children }: { children: React.ReactNode }) {
  return <div className={styles.frame}>{children}</div>;
}

function StatusBanners() {
  const online = useOnline();
  const update = useUpdateState();
  const { platform } = useAppServices();

  return (
    <>
      {update.status === "available" ? (
        <div className={styles.updateBar} role="status">
          <span>A new Vocably version is ready.</span>
          <button type="button" onClick={() => void platform.updates.accept()}>
            Update
          </button>
        </div>
      ) : null}
      {update.status === "installing" ? (
        <div className={styles.updateBar} role="status">
          <span>Updating Vocably…</span>
        </div>
      ) : null}
      {update.status === "failed" ? (
        <div className={`${styles.updateBar} ${styles.updateBarFailed}`} role="status">
          <span>{update.message}</span>
        </div>
      ) : null}
      {!online ? (
        <div className={styles.offlineBar} role="status">
          You are offline. Saved words and practice still work.
        </div>
      ) : null}
    </>
  );
}

export function Screen({
  children,
  withNav = true,
}: {
  children: React.ReactNode;
  withNav?: boolean;
}) {
  return (
    <main className={`${styles.screen} ${withNav ? "" : styles.screenNoNav}`}>
      <StatusBanners />
      {children}
    </main>
  );
}

export function TopBar({
  title,
  backTo,
  onBack,
  right,
}: {
  title: string;
  backTo?: string;
  onBack?: () => void;
  right?: React.ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <div className={styles.topbar}>
      <button
        className={styles.iconButton}
        aria-label="Go back"
        type="button"
        onClick={() => {
          if (onBack) {
            onBack();
            return;
          }
          if (backTo) {
            navigate(backTo);
            return;
          }
          navigate(-1);
        }}
      >
        <ChevronLeft size={28} strokeWidth={1.75} />
      </button>
      <span className={styles.topbarTitle}>{title}</span>
      <span className={styles.topbarRight}>{right}</span>
    </div>
  );
}

function navClass(isActive: boolean): string {
  return isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink;
}

export function BottomNav() {
  return (
    <nav className={styles.nav} aria-label="Main navigation">
      <NavLink to="/" end className={({ isActive }) => navClass(isActive)}>
        {({ isActive }) => (
          <>
            <House className={styles.navIcon} size={22} strokeWidth={isActive ? 2.4 : 1.75} />
            Home
          </>
        )}
      </NavLink>
      <NavLink to="/words" className={({ isActive }) => navClass(isActive)}>
        {({ isActive }) => (
          <>
            <LayoutList className={styles.navIcon} size={22} strokeWidth={isActive ? 2.4 : 1.75} />
            My Words
          </>
        )}
      </NavLink>
      <NavLink to="/practice" className={({ isActive }) => navClass(isActive)}>
        {({ isActive }) => (
          <>
            <Zap className={styles.navIcon} size={22} strokeWidth={isActive ? 2.4 : 1.75} />
            Practice
          </>
        )}
      </NavLink>
    </nav>
  );
}

export function PrimaryButton(
  props: React.ButtonHTMLAttributes<HTMLButtonElement> & { secondary?: boolean },
) {
  const { secondary, className, type = "button", ...rest } = props;
  return (
    <button
      className={`${secondary ? styles.secondary : styles.primary} ${className ?? ""}`.trim()}
      type={type}
      {...rest}
    />
  );
}
